/**
 * Stripe's redirect back to the page that hosts the deposit payment panel
 * (`@/features/shared/deposit-payment`). Kept here, below the features, with
 * no import: a route's `beforeLoad` reads it, and importing the panel there
 * would load Stripe.js on every page.
 */

/**
 * The search keys Stripe adds when it sends the payer back after a bank's
 * approval step. The page hosting the deposit panel completes the payment
 * once for `payment_intent`, then removes all three from its URL (the client
 * secret must not stay in the address bar or its history).
 */
export const STRIPE_REDIRECT_SEARCH_KEYS = [
  "payment_intent",
  "payment_intent_client_secret",
  "redirect_status",
] as const;

type StripeRedirectSearchKey = (typeof STRIPE_REDIRECT_SEARCH_KEYS)[number];

const REDIRECT_KEYS: ReadonlySet<string> = new Set(STRIPE_REDIRECT_SEARCH_KEYS);

/**
 * The page's search without Stripe's redirect keys, for a replace navigation:
 * `navigate({ search: (prev) => withoutStripeRedirect(prev), replace: true })`.
 * It drops the keys itself, so a route whose `validateSearch` already strips
 * the two it does not declare still loses all three. PURE.
 *
 * @param search - The current search, left unchanged
 */
export function withoutStripeRedirect<T extends Record<string, unknown>>(
  search: T,
): Omit<T, StripeRedirectSearchKey> {
  // `Object.fromEntries` widens the keys to string; the filter removes exactly
  // the omitted keys and keeps every other entry as it was.
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !REDIRECT_KEYS.has(key)),
  ) as Omit<T, StripeRedirectSearchKey>;
}

/**
 * The two keys of Stripe's redirect that the page does not need: the
 * PaymentIntent's client secret, a credential, and the status that rides
 * with it. `payment_intent` stays, for the page to complete the payment.
 */
export const STRIPE_REDIRECT_SECRET_KEYS = [
  "payment_intent_client_secret",
  "redirect_status",
] as const;

type StripeRedirectSecretKey = (typeof STRIPE_REDIRECT_SECRET_KEYS)[number];

const SECRET_KEYS: ReadonlySet<string> = new Set(STRIPE_REDIRECT_SECRET_KEYS);

/** True when the search still holds a key of `STRIPE_REDIRECT_SECRET_KEYS`. PURE. */
export function hasStripeRedirectSecret(search: object): boolean {
  return Object.keys(search).some((key) => SECRET_KEYS.has(key));
}

/**
 * The search without the client secret and `redirect_status`, for a route to
 * redirect to before anything renders (`beforeLoad`), so no layout, tag
 * manager, page view or sign-in redirect ever reads the secret. Keeps
 * `payment_intent`, the option on show and every other key. PURE.
 *
 * @param search - The raw parsed search, left unchanged
 */
export function withoutStripeRedirectSecret<T extends Record<string, unknown>>(
  search: T,
): Omit<T, StripeRedirectSecretKey> {
  // As in `withoutStripeRedirect`: the filter removes exactly the omitted keys.
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => !SECRET_KEYS.has(key)),
  ) as Omit<T, StripeRedirectSecretKey>;
}
